import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, Check, Crosshair, Phone, RotateCcw, User, X,
} from 'lucide-react'
import { useFleet } from '../state/store'
import { ago, clock, num, SEVERITY_COLOR, unit, vehicleState } from '../lib/format'
import { requireSupervisor } from '../lib/supervisor'
import { rewriteStopMessage } from '../lib/stopPlace'
import { StatusBadge, Empty } from '../ui/primitives'
import type { Alert } from '../lib/types'

type TriageAction = 'ack' | 'attended' | 'called' | 'no_answer' | 'escalated'
  | 'resolve' | 'reopen'

interface AlertAction {
  action: string
  by_whom: string | null
  phone: string | null
  note: string | null
  ts: string
}

const ACTION_LABEL: Record<string, string> = {
  ack: 'Acknowledged', attended: 'Attended', called: 'Called',
  no_answer: 'No answer', escalated: 'Escalated', resolve: 'Resolved', reopen: 'Reopened',
}

/**
 * What a supervisor does with an alert.
 *
 * The tape answers "what happened". This answers "what do I do about it", which is a
 * different question and the only one that matters at 3am: who is driving, what is their
 * number, where is the bus now, what road did it take to get there, and what else has
 * gone wrong with it today. Then: acknowledge it so the next person knows it is being
 * handled, or resolve it.
 *
 * Every action here is real. Call opens the dialler. Locate flies the map. Nothing is a
 * placeholder, because a button that does nothing is worse than no button.
 */
export function AlertDetail({ alertId, fallback }: {
  alertId: string
  /** History-window alert not present on the live SSE tape. */
  fallback?: Alert | null
}): JSX.Element | null {
  const live = useFleet((s) => s.alerts.find((a) => a.alert_id === alertId))
  const alert = live ?? fallback ?? undefined
  const meta = useFleet((s) => (alert?.vehicle_id ? s.meta.get(alert.vehicle_id) : undefined))
  const vehicle = useFleet((s) => (alert?.vehicle_id ? s.vehicles.get(alert.vehicle_id) : undefined))
  const geofences = useFleet((s) => s.geofences)
  const triage = useFleet((s) => s.triage.get(alertId))
  const allAlerts = useFleet((s) => s.alerts)
  const close = useFleet((s) => s.openAlertDetail)
  const setTriage = useFleet((s) => s.setTriage)
  const selectVehicle = useFleet((s) => s.selectVehicle)
  const flyTo = useFleet((s) => s.flyTo)

  const [now, setNow] = useState(() => Date.now())
  const [actions, setActions] = useState<AlertAction[]>([])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  // Reload the trail whenever the panel switches alert, so a supervisor opening a second
  // alert never sees the first one's history.
  useEffect(() => {
    let live = true
    void (async () => {
      const r = await fetch(`/api/alerts/${alertId}/actions`)
      if (!r.ok || !live) return
      setActions(((await r.json()) as { actions: AlertAction[] }).actions ?? [])
    })()
    return () => { live = false }
  }, [alertId])

  // Everything else that has gone wrong with this bus, so a supervisor sees a pattern
  // rather than an isolated incident.
  const vehicleHistory = useMemo(() => {
    if (!alert?.vehicle_id) return []
    return allAlerts.filter((a) => a.vehicle_id === alert.vehicle_id && a.alert_id !== alertId)
  }, [allAlerts, alert?.vehicle_id, alertId])

  if (!alert) return null

  const state = triage?.state ?? 'new'
  const sev = SEVERITY_COLOR[alert.severity] ?? '#8890A8'
  const message = rewriteStopMessage(
    alert.message,
    vehicle?.lat ?? alert.lat,
    vehicle?.lon ?? alert.lon,
    geofences,
  )

  async function act(action: TriageAction, phone?: string): Promise<void> {
    // WHO, not just what. Every action used to be filed as by_whom='supervisor' -- a
    // constant -- so the trail could say an alert was attended and never who attended it.
    // A shift handover, a dispute over whether a driver was really called, and the
    // before/after intervention study all need the name. Self-declared and stored per
    // browser; see lib/supervisor.ts for why that is the right weight of evidence here.
    const by = requireSupervisor((msg, dflt) => window.prompt(msg, dflt))
    const q = new URLSearchParams({ by })
    if (phone) q.set('phone', phone)
    const r = await fetch(`/api/alerts/${alertId}/${action}?${q}`, { method: 'POST' })
    if (r.ok) {
      setTriage(await r.json())
      void loadActions()
    }
  }

  // The audit trail, read back from Postgres. Shown rather than kept invisible: the whole
  // reason it is append-only is so somebody can check it later, and a record nobody can
  // see is one nobody trusts.
  async function loadActions(): Promise<void> {
    const r = await fetch(`/api/alerts/${alertId}/actions`)
    if (r.ok) setActions(((await r.json()) as { actions: AlertAction[] }).actions ?? [])
  }

  function locate(): void {
    // SAME RULE AS CLICKING THE ALERT: the truck's live position first, the alert's own
    // coordinates only as a fallback. This used to prefer the alert's -- where the truck
    // WAS when the rule fired -- so for an idling or unscheduled-stop alert it flew to an
    // empty stretch of road the truck had left hours earlier.
    if (!alert) return
    if (alert.vehicle_id) selectVehicle(alert.vehicle_id)
    if (vehicle?.lat != null && vehicle.lon != null) {
      flyTo({ lon: vehicle.lon, lat: vehicle.lat, zoom: 14 })
    } else if (alert.lat != null && alert.lon != null) {
      flyTo({ lon: alert.lon, lat: alert.lat, zoom: 14 })
    }
  }

  return (
    <aside className="absolute bottom-3 right-3 top-3 z-20 flex w-[400px] flex-col overflow-hidden
                      rounded-lg border border-line-strong bg-raised/97 shadow-2xl backdrop-blur
                      animate-slide-up">
      <header className="shrink-0 border-b border-line px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle size={13} style={{ color: sev }} />
              <span className="font-display text-sm font-semibold tracking-tight" style={{ color: sev }}>
                {alert.kind.replace(/_/g, ' ')}
              </span>
              <TriagePill state={state} by={triage?.by} />
            </div>
            <p className="mt-1.5 text-base leading-snug text-ink">{message}</p>
            <p className="mt-1 text-2xs text-faint">
              {/* Date AND time. A lap here runs 22-28 hours and shifts cross midnight, so
                  a bare clock reading is ambiguous about which day it belongs to -- which
                  matters the moment somebody matches it against a call log. */}
              {new Date(alert.ts).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                second: '2-digit', hour12: false,
              })} · {ago(alert.ts, now)} ago
              {alert.cell && ` · cell ${alert.cell.slice(0, 9)}…`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => close(null)}
                    className="rounded p-1 text-muted transition-colors hover:bg-high hover:text-ink"
                    aria-label="close" title="Close">
              <X size={15} />
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto px-4 py-3">
        {/* ---------------------------------------------------------------- who to call */}
        {alert.vehicle_id ? (
          <section>
            <h3 className="label mb-2">Crew</h3>
            <div className="card p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-high">
                    <User size={14} className="text-muted" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-base text-ink">
                      {meta?.driver_name ?? 'Driver unassigned'}
                    </p>
                    <p className="truncate text-2xs text-faint">
                      {alert.vehicle_id}
                      {meta?.depot && ` · ${meta.depot}`}
                      {meta?.co_driver_name && ` · with ${meta.co_driver_name}`}
                    </p>
                  </div>
                </div>
                {meta?.driver_phone ? (
                  <a
                    href={`tel:${meta.driver_phone}`}
                    // Recording happens HERE, on the dialler itself, because this is the
                    // moment the call actually starts. A separate "log the call" button
                    // records intent and gets skipped under pressure; this records the act
                    // and costs the operator nothing. Default is not prevented -- the
                    // dialler still opens.
                    onClick={() => void act('called', meta.driver_phone ?? undefined)}
                    className="flex shrink-0 items-center gap-1.5 rounded bg-brand px-2.5 py-1.5
                               text-2xs font-medium text-bg transition-opacity hover:opacity-85"
                  >
                    <Phone size={12} /> Call
                  </a>
                ) : (
                  <span className="text-2xs text-faint">no number</span>
                )}
              </div>
              {meta?.driver_phone && (
                <p className="num mt-2 border-t border-line pt-2 font-mono text-2xs text-muted">
                  {meta.driver_phone}
                </p>
              )}
            </div>
          </section>
        ) : (
          <section>
            <h3 className="label mb-2">Scope</h3>
            <p className="text-xs leading-relaxed text-muted">
              This is a network alert, not a vehicle one — it belongs to a road cell, so
              there is no single driver to call. Use Locate to see which buses are in it.
            </p>
          </section>
        )}

        {/* ------------------------------------------------------------------- actions */}
        <section>
          <h3 className="label mb-2">Act</h3>
          <div className="grid grid-cols-2 gap-1.5">
            <Action icon={<Crosshair size={12} />} onClick={locate}>Locate on map</Action>
            {state === 'new' && (
              <Action icon={<Check size={12} />} tone="brand" onClick={() => act('ack')}>
                Acknowledge
              </Action>
            )}
            {state === 'acknowledged' && (
              <Action icon={<Check size={12} />} tone="brand" onClick={() => act('resolve')}>
                Resolve
              </Action>
            )}
            {state !== 'new' && (
              <Action icon={<RotateCcw size={12} />} onClick={() => act('reopen')}>Reopen</Action>
            )}
          </div>

          {/* The two that get audited. Only on the severities the operation actually
              staffs -- putting them on INFO would collect thousands of rows nobody will
              ever check, and dilute the ones that matter. */}
          {(alert.severity === 'CRITICAL' || alert.severity === 'WARN') && (
            <div className="mt-1.5 grid grid-cols-3 gap-1.5">
              <Action icon={<Check size={12} />} onClick={() => act('attended')}>Attended</Action>
              <Action icon={<Phone size={12} />}
                      onClick={() => act('called', meta?.driver_phone ?? undefined)}>
                Called
              </Action>
              {/* The operator did the work and the driver did not pick up. Recorded as its
                  own outcome so it is not filed as "nobody attended", which blames the
                  wrong party and teaches people to stop logging it. */}
              <Action icon={<X size={12} />} onClick={() => act('no_answer')}>No answer</Action>
            </div>
          )}

          {actions.length > 0 && (
            <div className="mt-2 border-t border-line pt-2">
              <p className="label mb-1">Recorded</p>
              <ul className="space-y-0.5">
                {actions.map((a, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-2 text-2xs">
                    <span className="text-muted">
                      {ACTION_LABEL[a.action] ?? a.action}
                      {a.by_whom && <span className="text-faint"> · {a.by_whom}</span>}
                      {a.phone && <span className="num font-mono text-faint"> · {a.phone}</span>}
                    </span>
                    {/* Postgres returns a timestamp string; clock() speaks epoch ms. */}
                    <span className="shrink-0 text-faint">{clock(Date.parse(a.ts))}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* --------------------------------------------------------------- bus right now */}
        {vehicle && (
          <section>
            <h3 className="label mb-2">Vehicle now</h3>
            <div className="card grid grid-cols-2 gap-x-3 gap-y-1 p-3">
              <Row label="Status">
                <StatusBadge status={vehicleState(vehicle, now)} />
              </Row>
              <Row label="Speed">{unit(vehicle.speed_kph, 'KMPH')}</Row>
              <Row label="SoC">{vehicle.soc_pct == null ? '—' : `${vehicle.soc_pct}%`}</Row>
              <Row label="Coolant">
                {vehicle.coolant_c == null ? '—' : `${num(vehicle.coolant_c, 0)}°C`}
              </Row>
              <Row label="Route">{meta?.route_id ?? '—'}</Row>
              <Row label="Last seen">
                {ago(Math.max(vehicle.last_obd_ts, vehicle.last_geo_ts), now)}
              </Row>
            </div>
          </section>
        )}

        {/* -------------------------------------------------------- what else went wrong */}
        <section>
          <h3 className="label mb-2">
            This vehicle today {vehicleHistory.length > 0 && `· ${vehicleHistory.length}`}
          </h3>
          {vehicleHistory.length === 0 ? (
            <p className="text-xs leading-relaxed text-faint">
              Nothing else from this bus in the retained window. An isolated incident.
            </p>
          ) : (
            <ul className="space-y-px">
              {vehicleHistory.slice(0, 12).map((a) => (
                <li key={a.alert_id}>
                  <button
                    onClick={() => close(a.alert_id)}
                    className="flex w-full items-baseline gap-2 rounded px-1.5 py-1 text-left
                               transition-colors hover:bg-high"
                  >
                    <span className="num shrink-0 font-mono text-2xs text-faint">{clock(a.ts)}</span>
                    <span className="shrink-0 text-2xs"
                          style={{ color: SEVERITY_COLOR[a.severity] ?? '#8890A8' }}>
                      {a.kind.replace(/_/g, ' ').toLowerCase()}
                    </span>
                    <span className="truncate text-2xs text-muted">
                      {rewriteStopMessage(
                        a.message,
                        vehicle?.lat ?? a.lat,
                        vehicle?.lon ?? a.lon,
                        geofences,
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </aside>
  )
}

function TriagePill({ state, by }: { state: string; by?: string }): JSX.Element {
  const cfg = {
    new: { c: 'bg-danger-dim text-danger', t: 'New' },
    acknowledged: { c: 'bg-warn-dim text-warn', t: by ? `Ack · ${by}` : 'Acknowledged' },
    resolved: { c: 'bg-ok-dim text-ok', t: 'Resolved' },
  }[state] ?? { c: 'bg-high text-muted', t: state }
  return <span className={`rounded-sm px-1.5 py-0.5 text-2xs ${cfg.c}`}>{cfg.t}</span>
}

function Action({
  icon, children, onClick, tone, disabled, busy,
}: {
  icon: JSX.Element; children: React.ReactNode; onClick: () => void
  tone?: 'brand'; disabled?: boolean; busy?: boolean
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      className={`flex items-center justify-center gap-1.5 rounded border px-2 py-1.5 text-2xs
                  transition-colors disabled:opacity-40
                  ${tone === 'brand'
                    ? 'border-brand-line bg-brand-dim text-brand hover:bg-brand/20'
                    : 'border-line text-muted hover:border-line-strong hover:text-ink'}`}
    >
      {icon}
      {busy ? 'Loading…' : children}
    </button>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-faint">{label}</span>
      <span className="num text-ink">{children}</span>
    </div>
  )
}

export { Empty }
